/* eslint-disable security/detect-object-injection -- every bracket
   lookup in this file reads from a constant Record<string,string>;
   the lookup returns `string | undefined` and we always fall back to
   `fallbackHumanize` on miss. There is no injection sink. */

// Human labels for enum values that the operator sees.
//
// Single source of truth so a value like `multi_family` never reaches
// the UI as raw snake_case ("multi_family" — what's that?). The portal
// is operated by property managers, not engineers; copy reads as
// natural English.
//
// Rules of thumb when adding a new mapping here:
//   - No underscores in the rendered string.
//   - No internal jargon ("master_admin scope", "TENANT_MANAGE_*", "outbox").
//   - Use natural operator-facing copy (usually sentence case).
//   - Short — usually 1–3 words. If it needs a sentence to explain
//     itself, that's a copy block elsewhere, not a label.
//
// Unknown / unmapped values fall back to a Title-Case humanization of
// the underscore-split input so new enum values don't crash; the
// fallback is good enough that adding a row here is the right fix but
// not a blocking one.

import type { SiteMode } from './setupStatus.js';

function fallbackHumanize(value: string): string {
  if (!value) return '';
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------- site mode (property type) ----------

const SITE_MODE_LABELS: Record<SiteMode, string> = {
  commercial_multi_tenant: 'Commercial / multi-tenant',
  single_tenant: 'Single organization',
  multi_family: 'Multi-family residential',
};

const SITE_MODE_SHORT_LABELS: Record<SiteMode, string> = {
  commercial_multi_tenant: 'Commercial',
  single_tenant: 'Single org',
  multi_family: 'Multi-family',
};

/** Full name — use in headings, definition lists, status banners. */
export function siteModeLabel(mode: SiteMode | string | null | undefined): string {
  if (!mode) return '';
  return SITE_MODE_LABELS[mode as SiteMode] ?? fallbackHumanize(mode);
}

/** Short form — use in chips, tight nav badges, dense tables. */
export function siteModeShortLabel(
  mode: SiteMode | string | null | undefined,
): string {
  if (!mode) return '';
  return SITE_MODE_SHORT_LABELS[mode as SiteMode] ?? fallbackHumanize(mode);
}

// ---------- roles ----------

type Role =
  | 'master_admin'
  | 'tenant_admin'
  | 'tenant_manager'
  | 'tenant_viewer'
  | 'resident';

const ROLE_LABELS: Record<Role, string> = {
  master_admin: 'Super admin',
  tenant_admin: 'Tenant admin',
  tenant_manager: 'Tenant manager',
  tenant_viewer: 'Tenant viewer',
  resident: 'Resident',
};

export function roleLabel(role: string | null | undefined): string {
  if (!role) return '';
  return ROLE_LABELS[role as Role] ?? fallbackHumanize(role);
}

// ---------- user / tenant / employee status ----------

const STATUS_LABELS: Record<string, string> = {
  // user / employee / tenant share most values
  active: 'Active',
  invited: 'Invited',
  suspended: 'Suspended',
  archived: 'Archived',
  // employee-specific
  offboarded: 'Removed',
  // device / scope
  online: 'Online',
  offline: 'Offline',
  unknown: 'Unknown',
};

export function statusLabel(status: string | null | undefined): string {
  if (!status) return '';
  return STATUS_LABELS[status] ?? fallbackHumanize(status);
}

// ---------- employee role (NOT to be confused with portal Role) ----------

const EMPLOYEE_ROLE_LABELS: Record<string, string> = {
  employee: 'Employee',
  manager: 'Manager',
};

export function employeeRoleLabel(role: string | null | undefined): string {
  if (!role) return '';
  return EMPLOYEE_ROLE_LABELS[role] ?? fallbackHumanize(role);
}

// ---------- credential type ----------

const CREDENTIAL_TYPE_LABELS: Record<string, string> = {
  nfc_card: 'NFC card',
  mobile_key: 'Phone KeyPass',
  pin_code: 'PIN code',
};

export function credentialTypeLabel(
  type: string | null | undefined,
  opts?: {
    unifiCredentialId?: string | null;
    touchPassId?: string | null;
    surface?: 'employee' | 'resident_guest' | 'mobile_activation';
  },
): string {
  if (!type) return '';
  if (type === 'mobile_key') {
    if (opts?.touchPassId) return 'Phone KeyPass';
    if (opts?.unifiCredentialId?.startsWith('touch:')) return 'Phone KeyPass';
    if (opts?.unifiCredentialId?.startsWith('pin:')) return 'PIN code';
    return 'Phone KeyPass';
  }
  return CREDENTIAL_TYPE_LABELS[type] ?? fallbackHumanize(type);
}

/** Short label for credential pill badges. */
export function credentialTypeShortLabel(type: string): string {
  const SHORT: Record<string, string> = {
    nfc_card: 'Key fob',
    pin_code: 'PIN',
    mobile_key: 'Mobile',
    touch: 'Touch',
  };
  return SHORT[type] ?? credentialTypeLabel(type);
}

// ---------- credential status ----------

const CREDENTIAL_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  active: 'Active',
  revoked: 'Revoked',
};

export function credentialStatusLabel(status: string | null | undefined): string {
  if (!status) return '';
  return CREDENTIAL_STATUS_LABELS[status] ?? fallbackHumanize(status);
}

// ---------- sync status ----------

const SYNC_STATUS_LABELS: Record<string, string> = {
  synced: 'In sync',
  pending: 'Syncing…',
  retry: 'Retrying…',
  failed: 'Sync failed',
};

export function syncStatusLabel(status: string | null | undefined): string {
  if (!status) return '';
  return SYNC_STATUS_LABELS[status] ?? fallbackHumanize(status);
}

// ---------- amenity reservation status ----------

const AMENITY_RESERVATION_STATUS_LABELS: Record<string, string> = {
  booked: 'Booked',
  cancelled: 'Cancelled',
  no_show: 'No show',
  completed: 'Completed',
};

export function amenityReservationStatusLabel(
  status: string | null | undefined,
): string {
  if (!status) return '';
  return AMENITY_RESERVATION_STATUS_LABELS[status] ?? fallbackHumanize(status);
}

// ---------- intercom call status ----------

const INTERCOM_CALL_STATUS_LABELS: Record<string, string> = {
  queued: 'Ringing',
  answered: 'In call',
  ended: 'Ended',
  missed: 'Missed',
};

export function intercomCallStatusLabel(
  status: string | null | undefined,
): string {
  if (!status) return '';
  return INTERCOM_CALL_STATUS_LABELS[status] ?? fallbackHumanize(status);
}

// ---------- default-tenant health ----------

const DEFAULT_TENANT_HEALTH_LABELS: Record<string, string> = {
  ok: 'Healthy',
  missing: 'Missing',
  inactive: 'Inactive',
  unset: 'Not set',
};

export function defaultTenantHealthLabel(
  value: string | null | undefined,
): string {
  if (!value) return '';
  return DEFAULT_TENANT_HEALTH_LABELS[value] ?? fallbackHumanize(value);
}

// ---------- resource type ----------

const RESOURCE_TYPE_LABELS: Record<string, string> = {
  employee: 'Employee',
  credential: 'Credential',
  access_group: 'Access group',
  access_policy: 'Access policy',
  user_group: 'User group',
  amenity_reservation: 'Amenity reservation',
  vendor_access: 'Vendor access',
  intercom_call: 'Intercom call',
  household: 'Household',
  resident_profile: 'Resident',
  guest_pass: 'Guest pass',
  outbox_job: 'Sync job',
  schedule: 'Schedule',
  user: 'User',
  tenant: 'Tenant',
};

export function resourceTypeLabel(value: string | null | undefined): string {
  if (!value) return '';
  return RESOURCE_TYPE_LABELS[value] ?? fallbackHumanize(value);
}

// ---------- hardware ----------

const DEVICE_KIND_LABELS: Record<string, string> = {
  hub: 'Hub',
  reader: 'Reader',
  intercom: 'Intercom',
  door: 'Door',
  device: 'Device',
  unknown: 'Unknown device',
};

export function deviceKindLabel(value: string | null | undefined): string {
  if (!value) return '';
  return DEVICE_KIND_LABELS[value] ?? fallbackHumanize(value);
}

const LOCK_STATE_LABELS: Record<string, string> = {
  locked: 'Locked',
  unlocked: 'Unlocked',
  hold_open: 'Held open',
  keep_lock: 'Kept locked',
  keep_unlock: 'Kept unlocked',
  unknown: 'Unknown',
};

export function lockStateLabel(value: string | null | undefined): string {
  if (!value) return '';
  return LOCK_STATE_LABELS[value] ?? fallbackHumanize(value);
}

export function policyStateLabel(value: boolean): string {
  return value ? 'Enabled' : 'Disabled';
}

// ---------- generic helper ----------

/**
 * Last-resort prettifier — converts an unknown snake_case enum value to
 * Title Case. Prefer one of the typed helpers above; this exists for
 * one-off displays where the call site has no compile-time enum to
 * dispatch on (e.g. audit `before`/`after` JSON snapshots).
 */
export function humanize(value: string | null | undefined): string {
  if (!value) return '';
  return fallbackHumanize(value);
}
